//tryOnController.ts — owns the camera, the Decart session, outfit state and the cost guard

import type { RealTimeClient } from '@decartai/sdk'
import { type Character, characterPrompt, enhanceCharacter } from '../../shared/character'
import type { Garment } from '../../shared/garment'
import { type GarmentSlot, slotOrder } from '../../shared/garmentSlot'
import { outfitPrompt } from '../../shared/outfitPrompt'
import {
	type CatalogMode,
	decartModel,
	dockKind,
	type DockKind,
	type MirrorMode,
	PRICE_PER_SECOND_USD,
	resolveMode,
	scenePresets,
} from '../../shared/realtimeModel'
import { createSession } from '../api/client'
import { CameraError, openCamera, stopStream } from '../camera/devices'
import { rotateStream, type RotatedStream } from '../camera/rotatedStream'
import { loadPreference, savePreference, type VideoPreference } from '../config/videoPreference'
import type { CharacterOverride } from './characterOverride'
import { composeOutfitReference } from './outfitComposer'

export type ConnectionState =
	| 'idle'
	| 'connecting'
	| 'connected'
	| 'generating'
	| 'reconnecting'
	| 'disconnected'

//Decart's own measurement: each capture timestamp rides through the model and is matched to
//playout, so this is camera-to-screen lag inside the browser. It excludes the physical camera
//sensor and the display panel, which add a few frames on top.
export interface LatencyStats {
	readonly medianMs: number | null
	readonly p90Ms: number | null
	readonly firstFrameMs: number | null
	readonly samples: number
	readonly rttMs: number | null
}

export interface TryOnState {
	readonly connectionState: ConnectionState
	readonly outfit: Readonly<Partial<Record<GarmentSlot, Garment>>>
	readonly character: Character | null
	//Set by the admin's live catalogue: characters put the mirror in character mode.
	readonly catalogMode: CatalogMode
	readonly isPreviewing: boolean
	readonly isSwitching: boolean
	readonly billedSeconds: number
	readonly maxSessionSeconds: number
	//Only measured while the stats overlay is on; kept after a stop so it can still be read.
	readonly latency: LatencyStats | null
	//Set only by the avatar app: forces character mode and replaces the kiosk's Decart choices.
	readonly override: CharacterOverride | null
	readonly error: string | null
	readonly notice: string | null
	readonly preference: VideoPreference
}

const INITIAL: TryOnState = {
	connectionState: 'idle',
	outfit: {},
	character: null,
	catalogMode: 'lucy-vton-3.5',
	isPreviewing: false,
	isSwitching: false,
	billedSeconds: 0,
	maxSessionSeconds: 180,
	latency: null,
	override: null,
	error: null,
	notice: null,
	preference: loadPreference(),
}

export const isLive = (state: TryOnState): boolean =>
	state.connectionState === 'connected' || state.connectionState === 'generating'

export const isBusy = (state: TryOnState): boolean =>
	state.connectionState === 'connecting' || state.connectionState === 'reconnecting'

export const estimatedCostUSD = (state: TryOnState): number =>
	state.billedSeconds * PRICE_PER_SECOND_USD[decartModel(activeMode(state))]

//The admin's catalogue decides, unless this mirror has a local scene-model test override.
export const activeMode = (state: TryOnState): MirrorMode =>
	state.override ? 'character' : resolveMode(state.preference.model, state.catalogMode)

//Slot order, so the composited reference and the sentence agree on head-to-toe.
export const selectedGarments = (state: TryOnState): Garment[] =>
	Object.values(state.outfit)
		.filter((garment): garment is Garment => Boolean(garment))
		.sort((a, b) => slotOrder(a.slot) - slotOrder(b.slot))

export const selectedIds = (state: TryOnState): Set<string> =>
	new Set(selectedGarments(state).map((garment) => garment.id))

export const statusLabel = (state: TryOnState): string => {
	if (isLive(state)) return state.connectionState === 'generating' ? 'Live — billing' : 'Connected'
	if (isBusy(state)) return 'Connecting to Lucy…'
	return state.isPreviewing ? 'Preview — not billing' : 'Starting camera…'
}

export class TryOnController {
	private state: TryOnState = INITIAL
	private listeners = new Set<() => void>()

	//Media and session handles are deliberately outside the snapshot: they are not
	//renderable and must never trigger a re-render by identity.
	private rawStream: MediaStream | null = null
	private rotated: RotatedStream | null = null
	private client: RealTimeClient | null = null
	//Bumped on every stop. A connect that resolves after its session was stopped belongs to
	//nobody, so it is disconnected rather than installed — otherwise it bills until the cap.
	private sessionGeneration = 0
	private scenePromptTimer: number | undefined
	private remoteStream: MediaStream | null = null
	private onStreamChange: ((stream: MediaStream | null, live: boolean) => void) | null = null

	//Bumped on every teardown. A camera open that resolves after its generation has passed
	//belongs to a torn-down session, so it is stopped rather than installed — otherwise the
	//camera light stays on for a stream nothing is showing.
	private cameraGeneration = 0
	private previewInFlight: { generation: number; promise: Promise<void> } | null = null

	getSnapshot = (): TryOnState => this.state

	subscribe = (listener: () => void): (() => void) => {
		this.listeners.add(listener)
		return () => this.listeners.delete(listener)
	}

	//The video element is imperative, so it is fed by callback rather than through state.
	//Returns an unregister so the caller's effect cleanup owns the lifetime; clearing it from
	//teardown instead would race a StrictMode remount and leave the mirror blank.
	setStreamSink(sink: (stream: MediaStream | null, live: boolean) => void): () => void {
		this.onStreamChange = sink
		this.publishStreamToSink()
		return () => {
			if (this.onStreamChange === sink) this.onStreamChange = null
		}
	}

	private set(patch: Partial<TryOnState>): void {
		this.state = { ...this.state, ...patch }
		this.listeners.forEach((listener) => listener())
	}

	private publishStreamToSink(): void {
		const live = this.remoteStream !== null
		this.onStreamChange?.(this.remoteStream ?? this.publishedStream(), live)
	}

	private publishedStream(): MediaStream | null {
		return this.rotated?.stream ?? this.rawStream
	}

	//Runs the camera with no Decart session, so opening the app costs nothing and framing,
	//rotation and camera choice can all be checked before spending credits.
	//Bounded rather than a single attempt: a teardown can invalidate an open that is already
	//awaiting, and something has to notice and try again for the new generation or the mirror
	//sits on "Starting camera…" with the camera light on.
	async startPreview(attemptsRemaining = 3): Promise<void> {
		//Already running: re-publish rather than returning silently, so a sink that registered
		//after the camera came up still gets the stream.
		if (this.rawStream) {
			this.publishStreamToSink()
			return
		}
		if (attemptsRemaining <= 0) return

		//Single-flight per generation. Sharing the promise stops two overlapping calls each
		//opening a camera; keying it to the generation stops a caller adopting an attempt that
		//a teardown has already invalidated.
		const generation = this.cameraGeneration
		if (this.previewInFlight?.generation !== generation) {
			this.previewInFlight = { generation, promise: this.openPreview(generation) }
		}
		const attempt = this.previewInFlight
		try {
			await attempt.promise
		} finally {
			if (this.previewInFlight === attempt) this.previewInFlight = null
		}

		if (!this.rawStream && this.cameraGeneration !== generation) {
			await this.startPreview(attemptsRemaining - 1)
		}
	}

	private async openPreview(generation: number): Promise<void> {
		try {
			const { deviceId, captureRotation } = this.state.preference
			const swapOrientation = captureRotation === 1 || captureRotation === 3
			const stream = await openCamera({ deviceId, swapOrientation })
			if (generation !== this.cameraGeneration) {
				stopStream(stream)
				return
			}
			this.rawStream = stream
			this.rotated = rotateStream(stream, captureRotation)
			this.set({ isPreviewing: true, error: null })
			this.publishStreamToSink()
		} catch (cause) {
			if (generation !== this.cameraGeneration) return
			this.set({
				isPreviewing: false,
				error: cause instanceof CameraError ? cause.message : `Camera failed: ${String(cause)}`,
			})
		}
	}

	private teardownCamera(): void {
		this.cameraGeneration += 1
		this.rotated?.stop()
		this.rotated = null
		stopStream(this.rawStream)
		this.rawStream = null
	}

	async stopPreview(): Promise<void> {
		this.teardownCamera()
		this.set({ isPreviewing: false })
		this.publishStreamToSink()
	}

	//Tapping toggles the garment within its slot: tap again to take it off.
	async select(garment: Garment): Promise<void> {
		const current = this.state.outfit[garment.slot]
		const outfit = { ...this.state.outfit }
		if (current?.id === garment.id) {
			delete outfit[garment.slot]
		} else {
			outfit[garment.slot] = garment
		}
		this.set({ outfit })

		//Staging only while previewing, so starting Lucy stays a deliberate act. Scene
		//models ignore garments entirely, so there is nothing to apply mid-session.
		if (isLive(this.state) && dockKind(activeMode(this.state)) === 'garments') await this.applyOutfit()
	}

	//One character at a time; tapping another swaps it in place, without reconnecting.
	async selectCharacter(character: Character): Promise<void> {
		if (this.state.character?.id === character.id) return
		this.set({ character })
		if (isLive(this.state) && dockKind(activeMode(this.state)) === 'characters') {
			await this.applyCharacter()
		}
	}

	clearOutfit(): void {
		this.set({ outfit: {} })
	}

	//A running session belongs to the mode it started in, so a catalogue change that moves the
	//mirror to another mode ends it rather than sending, say, a character to a try-on session.
	setCatalogMode(catalogMode: CatalogMode): void {
		if (catalogMode === this.state.catalogMode) return
		const before = activeMode(this.state)
		this.set({ catalogMode })
		if ((isLive(this.state) || isBusy(this.state)) && activeMode(this.state) !== before) {
			this.set({ notice: 'The live catalogue changed. Start again.' })
			void this.stop()
		}
	}

	//The fallbacks make the panel's action always do something: starting with nothing picked
	//adopts the first product or character rather than refusing.
	async start(fallback?: Garment, fallbackCharacter?: Character): Promise<void> {
		if (isLive(this.state) || isBusy(this.state)) return
		const mode = activeMode(this.state)
		const dock = dockKind(mode)
		if (dock === 'garments' && Object.keys(this.state.outfit).length === 0 && fallback) {
			this.set({ outfit: { [fallback.slot]: fallback } })
		}
		if (dock === 'characters' && !this.state.character && fallbackCharacter) {
			this.set({ character: fallbackCharacter })
		}
		const garments = dock === 'garments' ? selectedGarments(this.state) : []
		if (dock === 'garments' && garments.length === 0) {
			this.set({ error: 'Pick at least one garment before starting Lucy.' })
			return
		}
		if (dock === 'characters' && !this.state.character) {
			this.set({ error: 'Pick a character before starting Lucy.' })
			return
		}

		this.set({ connectionState: 'connecting', error: null, notice: null, billedSeconds: 0, latency: null })
		const generation = this.sessionGeneration
		const isCurrent = () => generation === this.sessionGeneration

		try {
			//The camera may not be up yet if permission was only just granted.
			if (!this.rawStream) await this.startPreview()
			const stream = this.publishedStream()
			if (!stream) throw new Error('The camera is not running.')

			//Imported here rather than at module scope: the SDK pulls in LiveKit (~510kB), and
			//preview-first means nobody needs it until they actually commit to billing.
			const [{ createDecartClient, models }, session] = await Promise.all([
				import('@decartai/sdk'),
				createSession(decartModel(mode)),
			])
			//Short-lived, model-scoped and origin-pinned — minted by the Worker, which is the
			//only place the permanent key exists.
			const decart = createDecartClient({ apiKey: session.apiKey })
			const initialState = await this.initialLucyState(dock, garments)

			const client = await decart.realtime.connect(stream, {
				model: models.realtime(session.model as Parameters<typeof models.realtime>[0]),
				//The model's output cap (Decart serves Lucy at 720p only; 1080p is ignored, tested
				//2026-10-08); capture shape already follows the screen.
				resolution: '720p',
				//Pre-flips the published frames so the output reads like a mirror.
				mirror: this.state.preference.mirror,
				//Experimental in the SDK (frame-metadata worker), so only on while someone is watching.
				debugQuality: this.state.preference.showUpscaleStats,
				onRemoteStream: (remote) => {
					if (!isCurrent()) return
					this.remoteStream = remote
					this.publishStreamToSink()
				},
				onConnectionChange: (next) => {
					if (isCurrent()) this.set({ connectionState: next })
				},
				//The first frame is already transformed, so the wearer never sees the raw feed.
				initialState,
			})

			if (!isCurrent()) {
				client.disconnect()
				return
			}
			this.client = client
			this.set({ maxSessionSeconds: session.maxSessionSeconds })
			this.attachEvents(client)
		} catch (cause) {
			if (!isCurrent()) return
			this.set({ connectionState: 'idle', error: describe(cause) })
			await this.stop()
		}
	}

	private async initialLucyState(dock: DockKind, garments: readonly Garment[]) {
		const character = this.state.character
		if (dock === 'garments') {
			const image = await composeOutfitReference(garments)
			return {
				prompt: { text: outfitPrompt(garments), enhance: garments.some((g) => g.enrichPrompt) },
				...(image ? { image } : {}),
			}
		}
		if (dock === 'characters' && character) {
			const { prompt, enhance, image } = await this.characterLucyState(character)
			return { prompt: { text: prompt, enhance }, ...(image ? { image } : {}) }
		}
		//Scene models keep Decart's enhance default on: presets are short creative direction,
		//and server-side expansion is what makes them land.
		return { prompt: { text: scenePromptText(this.state), enhance: true } }
	}

	private attachEvents(client: RealTimeClient): void {
		//Billed seconds come from the server, so the figure on screen is what is charged
		//rather than a local stopwatch that drifts from it.
		client.on('generationTick', ({ seconds }) => this.set({ billedSeconds: seconds }))
		client.on('generationEnded', ({ seconds, reason }) => {
			this.set({
				billedSeconds: seconds,
				notice: `Session ended after ${seconds}s (${reason}).`,
			})
			void this.stop()
		})
		client.on('error', (error) => this.set({ error: `${error.code}: ${error.message}` }))
		if (!this.state.preference.showUpscaleStats) return
		client.on('stats', ({ glassToGlass, connection }) => {
			if (!glassToGlass) return
			this.set({
				latency: {
					medianMs: glassToGlass.medianMs,
					p90Ms: glassToGlass.p90Ms,
					firstFrameMs: glassToGlass.ttffMs,
					samples: glassToGlass.sampleCount,
					rttMs: connection.currentRoundTripTime === null ? null : connection.currentRoundTripTime * 1000,
				},
			})
		})
	}

	//Swapping garments reuses the open stream — reconnecting would cost a visible freeze and
	//buys nothing. Lucy replaces the whole look on every send, so the app always transmits
	//the complete outfit, never a delta.
	private async applyOutfit(): Promise<void> {
		const client = this.client
		if (!client || this.state.isSwitching) return
		const garments = selectedGarments(this.state)
		if (garments.length === 0) return

		this.set({ isSwitching: true })
		try {
			const reference = await composeOutfitReference(garments)
			await client.set({
				prompt: outfitPrompt(garments),
				enhance: garments.some((garment) => garment.enrichPrompt),
				...(reference ? { image: reference } : {}),
			})
			this.set({ error: null })
		} catch (cause) {
			this.set({ error: `Could not switch outfit: ${describe(cause)}` })
		} finally {
			this.set({ isSwitching: false })
		}
	}

	private async applyCharacter(): Promise<void> {
		const client = this.client
		const character = this.state.character
		if (!client || !character || this.state.isSwitching) return

		this.set({ isSwitching: true })
		try {
			const { prompt, enhance, image } = await this.characterLucyState(character)
			//set() replaces Lucy's whole state, so prompt and image travel together or one is cleared.
			await client.set({ prompt, enhance, ...(image ? { image } : {}) })
			this.set({ error: null })
		} catch (cause) {
			this.set({ error: `Could not switch character: ${describe(cause)}` })
		} finally {
			this.set({ isSwitching: false })
		}
	}

	//The stored image goes as uploaded. The kiosk derives enhance from the description; the
	//avatar app's setting overrides it.
	private async characterLucyState(character: Character) {
		return {
			prompt: characterPrompt(character),
			enhance: this.state.override?.enhance ?? enhanceCharacter(character),
			image: await characterReference(character),
		}
	}

	//Avatar app only; a change reaches a live session on applyOverride().
	setOverride(override: CharacterOverride): void {
		this.set({ override })
	}

	async applyOverride(): Promise<void> {
		if (isLive(this.state)) await this.applyCharacter()
	}

	//An admin save to the picked character arrives over the avatar's live channel. A new prompt or
	//image reaches a running session at once; a rename alone does not regenerate the look.
	async updateCharacter(character: Character): Promise<void> {
		const current = this.state.character
		if (current?.id !== character.id) return
		const looksDifferent = current.prompt !== character.prompt || current.imageURL !== character.imageURL
		this.set({ character })
		if (looksDifferent && isLive(this.state)) await this.applyCharacter()
	}

	private async applyScenePrompt(): Promise<void> {
		const client = this.client
		if (!client) return
		try {
			await client.set({ prompt: scenePromptText(this.state), enhance: true })
			this.set({ error: null })
		} catch (cause) {
			this.set({ error: `Could not update the prompt: ${describe(cause)}` })
		}
	}

	//Camera and capture rotation change the published track. replaceVideoTrack swaps it in
	//place, so unlike the native app this no longer needs a reconnect.
	async updatePreference(patch: Partial<VideoPreference>): Promise<void> {
		const previous = this.state.preference
		const preference = savePreference({ ...previous, ...patch })
		this.set({ preference })

		//Scene prompt edits reach the live session debounced, so typing does not cost a
		//regeneration per keystroke.
		if (patch.scenePrompt !== undefined && this.client && dockKind(activeMode(this.state)) === 'presets') {
			clearTimeout(this.scenePromptTimer)
			this.scenePromptTimer = window.setTimeout(() => void this.applyScenePrompt(), 600)
		}

		const needsNewTrack =
			preference.deviceId !== previous.deviceId ||
			preference.captureRotation !== previous.captureRotation

		if (!needsNewTrack) {
			this.publishStreamToSink()
			return
		}

		this.teardownCamera()
		await this.startPreview()

		const track = this.publishedStream()?.getVideoTracks()[0]
		if (this.client && track) {
			try {
				await this.client.replaceVideoTrack(track)
			} catch (cause) {
				this.set({ error: `Could not switch camera mid-session: ${describe(cause)}` })
			}
		}
	}

	async stop(): Promise<void> {
		this.sessionGeneration += 1
		this.client?.disconnect()
		this.client = null
		this.remoteStream = null
		this.set({ connectionState: 'idle' })
		//Disconnecting leaves the camera running: the mirror should keep showing a preview
		//between sessions rather than going black.
		this.publishStreamToSink()
	}

	//Media and session only. Listeners and the stream sink are owned by their subscribers'
	//own cleanups — clearing them here would race a StrictMode remount that has already
	//re-subscribed, leaving a live camera with nothing rendering it.
	async dispose(): Promise<void> {
		//Camera first, synchronously: this bumps the generation before any await yields, so a
		//remount that re-runs startPreview immediately sees the invalidation and opens fresh.
		this.teardownCamera()
		await this.stop()
	}

	dismissError(): void {
		this.set({ error: null, notice: null })
	}
}

//An empty prompt would ask the model to transform the scene into nothing; the first
//preset doubles as the highlighted default in the dock, so what you see is what runs.
function scenePromptText(state: TryOnState): string {
	return state.preference.scenePrompt.trim() || scenePresets(activeMode(state))[0].prompt
}

//Sent as a Blob: the image URL is same-origin and relative, which Decart's servers cannot fetch.
async function characterReference(character: Character): Promise<Blob | null> {
	if (!character.imageURL) return null
	const response = await fetch(character.imageURL)
	if (!response.ok) throw new Error(`Could not load the character image (HTTP ${response.status})`)
	return response.blob()
}

function describe(cause: unknown): string {
	if (cause instanceof Error) return cause.message
	return String(cause)
}
